import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Card from '@/components/Card';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Pagination from '@/components/Pagination';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextInput from '@/components/TextInput';
import TextStack from '@/components/TextStack';
import { Body } from '@/components/Typography';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { axiosRubyClient } from '@/util/axios/axiosClient';

const HEADER_FIELDS = [
  { name: 'ID', style: { width: '5%' } },
  { name: 'ORDER ID', style: { width: '8%' } },
  { name: 'WORKER', style: { width: '15%' } },
  { name: 'SKILL', style: { width: '12%' } },
  { name: 'SCHEDULE', style: { width: '12%' } },
  { name: 'SHIFT DATES', style: { width: '15%' } },
  { name: 'INVITE TYPE', style: { width: '8%' } },
  { name: 'OFFER STATUS', style: { width: '10%' } },
  { name: 'CREATED AT', style: { width: '15%' } },
];

type OrderInvite = {
  id: number;
  order_id: number;
  worker_id: string;
  worker_name: string;
  job_skill_id: string;
  job_skill_name: string;
  schedule_group_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  shift_dates: string[];
  invite_type: 'invited' | 'hired';
  job_offer_status: string | null;
  created_at: string;
  updated_at: string;
};

type PaginationData = {
  total: number;
  current_page: number;
  per_page: number;
  total_pages: number;
};

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const formatShortDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd');
  } catch (e) {
    return dateString;
  }
};

const getInviteTypeChip = (inviteType: string) => {
  switch (inviteType) {
    case 'hired':
      return <Chip color="success" label="Hired" size="small" />;
    case 'invited':
      return <Chip color="info" label="Invited" size="small" />;
    default:
      return <Chip label={inviteType} size="small" />;
  }
};

const getOfferStatusChip = (status: string | null) => {
  if (!status) return <Chip label="N/A" size="small" />;

  switch (status.toLowerCase()) {
    case 'accepted':
      return <Chip color="success" label="Accepted" size="small" />;
    case 'pending':
      return <Chip color="warning" label="Pending" size="small" />;
    case 'rejected':
      return <Chip color="error" label="Rejected" size="small" />;
    default:
      return (
        <Chip
          style={{ textTransform: 'capitalize' }}
          label={status}
          size="small"
        />
      );
  }
};

const OrderInvites: React.FC = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState<OrderInvite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    current_page: 1,
    per_page: 25,
    total_pages: 0,
  });
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchInvites();
  }, []);

  // Debounce search with 500ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== orderIdFilter) {
        setOrderIdFilter(searchInput);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        fetchInvites(searchInput, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, orderIdFilter]);

  const fetchInvites = async (orderId?: string, page: number = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page,
        limit: pagination.per_page,
      };

      const filterOrderId = orderId ?? orderIdFilter;
      if (filterOrderId) {
        params.order_id = filterOrderId;
      }

      const response = await axiosRubyClient.get('/api/admin/order_invites', {
        params,
      });

      setInvites(response.data.data || []);

      // Update pagination data from response
      const total = response.data.total || 0;
      const limit = response.data.limit || pagination.per_page;
      setPagination({
        total,
        current_page: page,
        per_page: limit,
        total_pages: Math.ceil(total / limit),
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching order invites:', err);
      setError('Error loading order invites');
      setInvites([]);
    } finally {
      setLoading(false);
    }
  };

  const onQueryChange = (value: string) => {
    setSearchInput(value);
  };

  const onPageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchInvites(orderIdFilter, page);
  };

  return (
    <Page
      size="full"
      title="Order Invites"
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: () => fetchInvites(orderIdFilter, pagination.current_page),
      }}
    >
      <Card>
        <Card.Section>
          <Stack gap={phoneOnly ? 16 : 8} vertical={phoneOnly}>
            <div style={{ flex: 1, maxWidth: 300 }}>
              <TextInput
                id="input-filter-order-id"
                placeholder="Filter by Order ID..."
                value={searchInput}
                onChange={(ev) => onQueryChange(ev.currentTarget.value)}
              />
            </div>
          </Stack>
        </Card.Section>

        <Table spacing={false}>
          <TableHeader fields={HEADER_FIELDS} />
          <tbody>
            {!loading && invites.length > 0 ? (
              invites.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Body>{item.id}</Body>
                  </TableCell>
                  <TableCell>
                    <Link to={`/orders/${item.order_id}`}>
                      <Body color="theme" weight="medium">
                        {item.order_id}
                      </Body>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Stack gap={4} vertical>
                      <Link to={`/workers/${item.worker_id}`}>
                        <Body color="theme" weight="medium">
                          {item.worker_name}
                        </Body>
                      </Link>
                      <Body size="sm" color="light">
                        ID: {item.worker_id}
                      </Body>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={4} vertical>
                      <Body weight="medium">{item.job_skill_name}</Body>
                      <Body size="sm" color="light">
                        Group: {item.schedule_group_id}
                      </Body>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={4} vertical>
                      <Body>
                        {item.start_date} - {item.end_date}
                      </Body>
                      <Body size="sm" color="light">
                        {item.start_time} - {item.end_time}
                      </Body>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={4} style={{ flexWrap: 'wrap' }}>
                      {item.shift_dates
                        ?.slice(0, 3)
                        .map((date, idx) => (
                          <Chip
                            key={idx}
                            label={formatShortDate(date)}
                            size="small"
                          />
                        ))}
                      {item.shift_dates?.length > 3 && (
                        <Chip
                          label={`+${item.shift_dates.length - 3} more`}
                          size="small"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{getInviteTypeChip(item.invite_type)}</TableCell>
                  <TableCell>
                    {getOfferStatusChip(item.job_offer_status)}
                  </TableCell>
                  <TableCell>
                    <TextStack spacing="tight">
                      <Body>{formatDate(item.created_at)}</Body>
                    </TextStack>
                  </TableCell>
                </TableRow>
              ))
            ) : !loading ? (
              <TableRow>
                <TableCell
                  colSpan={HEADER_FIELDS.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <Body>No order invites found.</Body>
                </TableCell>
              </TableRow>
            ) : null}
          </tbody>
        </Table>

        {loading && (
          <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
            <LoadingState overlayColor="white" text="Loading order invites" />
          </Stack>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
            <Body>{error}</Body>
          </div>
        )}

        {/* Pagination */}
        {!loading && invites.length > 0 && pagination.total_pages > 1 && (
          <Card.Section>
            <Stack justify="center">
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                onPageChange={onPageChange}
              />
            </Stack>
          </Card.Section>
        )}
      </Card>
    </Page>
  );
};

export default OrderInvites;
